// Documented project case studies. Shared by /projects/ (hub cards) and
// /gallery/ (gallery items that link back to the full project page).
import fireplaceHero from "@/assets/fireplace-fluted-finished-room.png.asset.json";
import cedarGroveFireplace from "@/assets/projects/finished-lime-plaster-fireplace-cedar-grove-new-jersey.webp.asset.json";
import coffiyehRoom from "@/assets/projects/limewash-coffiyeh-coffee-shop-walls-ceiling-clifton-nj.webp.asset.json";
import veiledCounter from "@/assets/projects/veiled-finished-counter.png.asset.json";
import travertineFireplace from "@/assets/projects/finished-travertine-plaster-fireplace-bernardsville-nj.webp.asset.json";
import franklinLakesBasement from "@/assets/projects/microcement-basement-franklin-lakes-nj-01.webp.asset.json";
import alpineKitchenIsland from "@/assets/projects/interior-painting-kitchen-island-coffered-ceiling-alpine-nj.webp.asset.json";
import waynePalladinoFireplace from "@/assets/projects/wayne-nj-brick-fireplace-before-after-palladino-plaster.webp.asset.json";
import haworthBeamLiving from "@/assets/projects/haworth-finished-walnut-beam-living-room.webp.asset.json";
import franklinLakesRenaissanceFireplace from "@/assets/projects/renaissance-venetian-plaster-fireplace-franklin-lakes-nj.webp.asset.json";
import glenRockDiningRoom from "@/assets/projects/finished-limewash-dining-room-glen-rock-nj.webp.asset.json";
import { paramusHero, PARAMUS_PATH } from "@/data/paramusLuxuryProject";
import { ridgewoodHero, RIDGEWOOD_PATH } from "@/data/ridgewoodPlasterProject";

export type CaseStudyCategory = "Decorative Plaster" | "Limewash" | "Microcement" | "Interior Painting" | "Commercial";

export interface CaseStudy {
  id: string;
  src: string;
  title: string;
  /** Short gallery title */
  shortTitle: string;
  location: string;
  service: string;
  finish: string;
  blurb: string;
  to: string;
  alt: string;
  categories: CaseStudyCategory[];
}

export const caseStudies: CaseStudy[] = [
  {
    id: "cs-ridgewood-plaster-walls-hood",
    src: ridgewoodHero.src,
    title: "Plaster Accent Walls & Travertine-Look Kitchen Hood",
    shortTitle: "Plaster Walls & Kitchen Hood",
    location: "Ridgewood, NJ",
    service: "Decorative Plaster",
    finish: "Palladino + Z-4 & Firenze Sand Finish",
    blurb: "A Palladino travertine-look kitchen hood and Firenze sand finish accent walls in Swiss Coffee and Overcast in one Ridgewood home.",
    to: RIDGEWOOD_PATH,
    alt: ridgewoodHero.alt,
    categories: ["Decorative Plaster"],
  },
  {
    id: "cs-paramus-luxury-new-construction",
    src: paramusHero.src,
    title: "Luxury New Construction Painting & Venetian Plaster",
    shortTitle: "Paramus Luxury New Construction",
    location: "Paramus, NJ",
    service: "Venetian Plaster & Interior Painting",
    finish: "Venetian Plaster, Palladino Lime Plaster & Color Drenching",
    blurb: "A luxury custom residence with handcrafted plaster accent walls, Palladino lime plaster bathrooms and complete interior painting throughout.",
    to: PARAMUS_PATH,
    alt: paramusHero.alt,
    categories: ["Decorative Plaster", "Interior Painting"],
  },
  {
    id: "cs-haworth-new-construction",
    src: haworthBeamLiving.url,
    title: "New Construction Interior Painting",
    shortTitle: "New Construction Interior Painting",
    location: "Haworth, NJ",
    service: "Interior Painting",
    finish: "Interior Paint & Walnut Refinishing",
    blurb:
      "A new custom home prepared, protected and painted throughout, with all walnut sanded and top coated in polyurethane.",
    to: "/projects/new-construction-interior-painting-haworth-nj/",
    alt: "Completed living room after new construction interior painting in Haworth NJ, with walnut ceiling beams",
    categories: ["Interior Painting"],
  },
  {
    id: "cs-wayne-palladino-fireplace",
    src: waynePalladinoFireplace.url,
    title: "Brick Fireplace to Palladino Plaster",
    shortTitle: "Palladino Plaster Fireplace",
    location: "Wayne, NJ",
    service: "Decorative Plaster",
    finish: "Palladino Plaster",
    blurb:
      "An existing brick fireplace cleaned, reinforced, skim coated and transformed with two hand-applied coats of Palladino plaster and a black-finished firebox.",
    to: "/projects/palladino-plaster-fireplace-wayne-nj/",
    alt: "Before and after brick fireplace transformation with Palladino plaster in Wayne, New Jersey",
    categories: ["Decorative Plaster"],
  },
  {
    id: "cs-cedar-grove",
    src: cedarGroveFireplace.url,
    title: "Palladino Lime Plaster Fireplace",
    shortTitle: "Palladino Lime Plaster Fireplace",
    location: "Cedar Grove, NJ",
    service: "Decorative Plaster",
    finish: "Meoded Palladino Lime Plaster",
    blurb:
      "A builder-grade fireplace stripped of molding and faux wood paneling, reinforced, skim coated and hand-finished in two coats of Meoded Palladino lime plaster.",
    to: "/projects/palladino-plaster-fireplace-cedar-grove-nj/",
    alt: "Completed lime plaster fireplace transformation by All Time Paint & Plaster Co. in Cedar Grove, New Jersey",
    categories: ["Decorative Plaster"],
  },
  {
    id: "cs-coffiyeh",
    src: coffiyehRoom.url,
    title: "Coffiyeh Coffee Shop Limewash",
    shortTitle: "Coffiyeh Coffee Shop Limewash",
    location: "Clifton, NJ",
    service: "Limewash",
    finish: "Hand-Applied Limewash + Stained Wood Counter",
    blurb:
      "Hand-applied limewash on the walls and ceilings of Coffiyeh coffee shop, with the new wood counter stained to match.",
    to: "/projects/limewash-coffee-shop-clifton-nj/",
    alt: "Limewashed walls and ceiling with copper pendant lights in the Coffiyeh coffee shop dining room in Clifton, NJ",
    categories: ["Limewash", "Commercial"],
  },
  {
    id: "cs-bernardsville",
    src: travertineFireplace.url,
    title: "Travertine-Look Palladino Fireplace",
    shortTitle: "Palladino Travertine Fireplace",
    location: "Bernardsville, NJ",
    service: "Decorative Plaster",
    finish: "Meoded Palladino + Z-4 Stone Additive",
    blurb:
      "Hand-applied Meoded Palladino with Z-4 Stone Additive, textured to create an organic, vein-cut travertine look on a custom fireplace.",
    to: "/projects/travertine-plaster-fireplace-bernardsville-nj/",
    alt: "Travertine-inspired Meoded Palladino plaster fireplace in Bernardsville, NJ",
    categories: ["Decorative Plaster"],
  },
  {
    id: "cs-franklin-lakes",
    src: franklinLakesBasement.url,
    title: "Whole-Basement Microcement Interior",
    shortTitle: "Microcement Basement Interior",
    location: "Franklin Lakes, NJ",
    service: "Microcement",
    finish: "Meoded Concretta — Casper",
    blurb:
      "A walk-out basement finished by hand in Meoded Concretta microcement on walls and ceilings, one seamless surface from hallway to entertainment room.",
    to: "/projects/microcement-basement-franklin-lakes-nj/",
    alt: "Seamless Meoded Concretta microcement basement walls and ceilings in Franklin Lakes, NJ",
    categories: ["Microcement"],
  },
  {
    id: "cs-franklin-lakes-limewash-fireplaces",
    src: franklinLakesRenaissanceFireplace.url,
    title: "Limewash & Plaster Work on Fireplaces",
    shortTitle: "Limewash & Plaster Fireplaces",
    location: "Franklin Lakes, NJ",
    service: "Limewash & Decorative Plaster",
    finish: "Creative White MC-748 Limewash + Renaissance & Palladino Plaster",
    blurb:
      "A two-story foyer and hallways limewashed in Creative White MC-748, with columns, a built-in TV wall and two marble fireplaces refinished in Meoded Renaissance and Palladino plaster.",
    to: "/projects/limewash-plaster-fireplaces-franklin-lakes-nj/",
    alt: "Marble fireplace resurfaced in Meoded Renaissance marbleized Venetian plaster in a Franklin Lakes, NJ home",
    categories: ["Limewash", "Decorative Plaster"],
  },
  {
    id: "cs-glen-rock-limewash-dining-entry",
    src: glenRockDiningRoom.url,
    title: "Warm White Limewash Dining Room & Entry",
    shortTitle: "Limewash Dining Room & Entry",
    location: "Glen Rock, NJ",
    service: "Limewash",
    finish: "Warm White Limewash (Meoded Lime Wash)",
    blurb:
      "A Glen Rock dining room and entry hallway finished in warm white limewash, hand-brushed above the existing white wainscoting for soft movement that changes with the light.",
    to: "/projects/limewash-dining-room-glen-rock-nj/",
    alt: "Finished warm white limewash dining room with brass chandelier and white wainscoting in Glen Rock, NJ",
    categories: ["Limewash"],
  },
  {
    id: "cs-veiled",
    src: veiledCounter.url,
    title: "Commercial Microcement Checkout Counter",
    shortTitle: "Microcement Checkout Counter",
    location: "Clifton, NJ",
    service: "Microcement",
    finish: "Meoded Concretta FS",
    blurb:
      "A custom retail checkout counter for the Veiled flagship store, meshed, primed and hand-finished in two coats of Meoded Concretta FS.",
    to: "/projects/microcement-countertop-clifton-nj/",
    alt: "Finished seamless microcement checkout counter at the Veiled flagship store in Clifton, NJ",
    categories: ["Microcement", "Commercial"],
  },
  {
    id: "cs-new-brunswick",
    src: fireplaceHero.url,
    title: "Fluted Venetian Plaster Fireplace",
    shortTitle: "Fluted Plaster Fireplace",
    location: "New Brunswick, NJ",
    service: "Decorative Plaster",
    finish: "Meoded Marmorino Intonaco",
    blurb:
      "A floor-to-ceiling fireplace surround hand-combed into continuous vertical flutes, then primed and sealed for daily use.",
    to: "/projects/venetian-plaster-fireplace-new-brunswick-nj/",
    alt: "Fluted plaster fireplace surround finished in Meoded Marmorino Intonaco in New Brunswick, NJ",
    categories: ["Decorative Plaster"],
  },
  {
    id: "cs-alpine",
    src: alpineKitchenIsland.url,
    title: "Estate Interior Painting & Wallpaper Hanging",
    shortTitle: "Alpine Estate Painting & Wallpaper",
    location: "Alpine, NJ",
    service: "Interior Painting",
    finish: "Full Interior Painting + Wallpaper Hanging",
    blurb:
      "Full interior painting of a large, newly renovated Alpine estate — every wall, ceiling, trim piece and door — plus every wallcovering in the home hung by our crew, including grasscloth and hand-painted panels.",
    to: "/projects/interior-painting-wallpaper-alpine-nj/",
    alt: "Painted coffered kitchen ceiling above a marble island in an Alpine, NJ estate",
    categories: ["Interior Painting"],
  },
];
